import { motion } from "framer-motion";
import { FileText, DollarSign, Calendar, ArrowRight } from "lucide-react";

const HowItWorks = () => {
  const steps = [
    {
      icon: FileText,
      number: "01",
      title: "Submit Property Info",
      description: "Fill out our simple form with basic details about your property. It takes less than 2 minutes.",
    },
    {
      icon: DollarSign,
      number: "02", 
      title: "Get a Cash Offer",
      description: "We'll review your property and present you with a fair, no-obligation cash offer within 24-48 hours.",
    },
    {
      icon: Calendar,
      number: "03",
      title: "Sell on Your Timeline",
      description: "Accept our offer and choose your closing date. We work around your schedule, not the other way around.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <section id="how-it-works" className="py-24 md:py-32 bg-slate-50" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-sm font-semibold text-[#D32F2F] uppercase tracking-wide mb-4">
            Simple Process
          </span>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            How It Works
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Selling your house shouldn't be complicated. Our streamlined process makes it easy to get cash for your home.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative"
            >
              {/* Connector Line (Desktop) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-[calc(50%+60px)] w-[calc(100%-120px)] h-0.5 bg-slate-200">
                  <ArrowRight className="absolute -right-3 -top-2.5 w-5 h-5 text-slate-300" />
                </div>
              )}

              {/* Card */}
              <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 feature-card h-full">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-[#D32F2F]/10 rounded-2xl flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-[#D32F2F]" />
                  </div>
                  <span 
                    className="absolute -top-2 -right-2 text-5xl font-bold text-slate-100"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {step.number}
                  </span>
                </div>

                {/* Content */}
                <h3 
                  className="text-xl font-bold text-slate-900 mb-3"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
